import React, { useState, useEffect } from 'react';
import { useSnackbar } from 'notistack';

// Por encima de esta vigencia (en minutos) ya no tiene sentido mostrar una
// cuenta regresiva en vivo (nadie espera un código de 1 día frente al modal);
// se muestra un texto estático y el backend sigue siendo quien realmente
// invalida el código al expirar.
const VIGENCIA_CORTA_MAXIMA_MINUTOS = 60;

const OTPModal = ({ isOpen, onClose, phoneNumberOTP, verifyFn, expiraEnMinutos = 5 }) => {
  const [otp, setOtp] = useState(Array(5).fill('')); // Array de 5 dígitos
  const [isValid, setIsValid] = useState(null);
  const esVigenciaCorta = expiraEnMinutos <= VIGENCIA_CORTA_MAXIMA_MINUTOS;
  const [timeLeft, setTimeLeft] = useState(expiraEnMinutos * 60);
  const [otpSent, setOtpSent] = useState(false);
  const [phoneNumber, setPhoneNumber] = useState(phoneNumberOTP);
  const [isVerifying, setIsVerifying] = useState(false);
  const [otpValidated, setOtpValidated] = useState(false);


  const { enqueueSnackbar } = useSnackbar();

  // Maneja el ingreso de cada dígito del OTP
  const handleInputChange = (e, index) => {
    const value = e.target.value;
    if (value.length <= 1 && /^[0-9]*$/.test(value)) {
      const updatedOtp = [...otp];
      updatedOtp[index] = value;
      setOtp(updatedOtp);
      // Mover al siguiente input si se ingresó un valor
      if (index < otp.length - 1 && value !== "") {
        document.getElementById(`otp-input-${index + 1}`).focus();
      }
    }
  };

  // Función para validar el OTP.
  // Toda la lógica (verificar + ejecutar acción) vive en el padre vía verifyFn.
  // El modal solo recolecta el código y delega.
  const ValidarCodigo = async () => {
    if (!verifyFn) return;
    const otpCode = otp.join('');
    setIsVerifying(true);
    enqueueSnackbar('Verificando OTP...', { variant: 'info', autoHideDuration: 2000 });
    try {
      const result = await verifyFn(otpCode);
      if (result?.success) {
        setOtpValidated(true);
        setIsValid(true);
      } else {
        setIsValid(false);
      }
    } catch (error) {
      console.error(error);
      setIsValid(false);
    } finally {
      setIsVerifying(false);
    }
  };

  // Cada vez que se abre el modal (código nuevo), reinicia el cronómetro con
  // la vigencia real que informó el backend para esta bodega.
  useEffect(() => {
    if (isOpen) setTimeLeft(expiraEnMinutos * 60);
  }, [isOpen, expiraEnMinutos]);

  // Actualización del cronómetro — solo para vigencias cortas (ver
  // VIGENCIA_CORTA_MAXIMA_MINUTOS). Con vigencia de horas/días el backend
  // sigue siendo quien invalida el código al expirar; el modal no se
  // autocierra por tiempo.
  useEffect(() => {
    if (!esVigenciaCorta) return;
    if (timeLeft > 0 && isOpen) {
      const timer = setInterval(() => {
        setTimeLeft((prevTime) => prevTime - 1);
      }, 1000);
      return () => clearInterval(timer);
    } else if (timeLeft <= 0) {
      onClose();
    }
  }, [isOpen, timeLeft, otpSent, phoneNumber, esVigenciaCorta]);

  // Formatea el tiempo en minutos y segundos
  const formatTime = (seconds) => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${String(minutes).padStart(2, '0')}:${String(remainingSeconds).padStart(2, '0')}`;
  };

  // Texto de vigencia para el mensaje de arriba del modal.
  const textoVigencia = (() => {
    if (expiraEnMinutos < 60) return `${expiraEnMinutos} minutos`;
    if (expiraEnMinutos % 1440 === 0) {
      const dias = expiraEnMinutos / 1440;
      return `${dias} día${dias > 1 ? 's' : ''}`;
    }
    const horas = Math.round(expiraEnMinutos / 60);
    return `${horas} hora${horas > 1 ? 's' : ''}`;
  })();

  return (
    isOpen && (
      <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-60">
        <div className="bg-white p-8 rounded-lg shadow-xl w-full sm:w-96">
          <h3 className="text-2xl font-semibold text-gray-800 mb-6">Verificación de OTP</h3>
          <p className="text-sm text-gray-600 mb-6">
            Se enviará un código de 5 dígitos a tu teléfono móvil {phoneNumber}. El código tendrá una duración de {textoVigencia}.
          </p>

          {/* Inputs para cada dígito del OTP */}
          <div className="flex justify-between mb-4">
            {otp.map((_, index) => (
              <input
                key={index}
                id={`otp-input-${index}`}
                type="text"
                maxLength="1"
                value={otp[index]}
                onChange={(e) => handleInputChange(e, index)}
                className="w-12 h-12 text-center text-2xl font-semibold border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="-"
              />
            ))}
          </div>

          {/* Cronómetro (solo vigencias cortas) o aviso estático (vigencias largas) */}
          <div className="mt-4 text-center">
            {esVigenciaCorta ? (
              <p className="text-sm text-gray-500">Tiempo restante: {formatTime(timeLeft)}</p>
            ) : (
              <p className="text-sm text-gray-500">Código vigente por {textoVigencia}.</p>
            )}
          </div>

          {/* Botón de validación del OTP */}
          <div className="mt-6">
            <button
              className="w-full py-2 bg-blue-600 text-white rounded-md font-semibold hover:bg-blue-700 disabled:opacity-50"
              onClick={ValidarCodigo}
              disabled={(esVigenciaCorta && timeLeft <= 0) || isVerifying || otpValidated}
              type="button"
            >
              Validar OTP
            </button>
          </div>

          {/* Mensaje de error en caso de fallo en la validación */}
          {isValid === false && (
            <p className="text-red-500 text-xs mt-2 text-center">
              El código OTP es incorrecto. Intenta de nuevo.
            </p>
          )}

          {/* Botón para cerrar el modal */}
          <div className="mt-6 text-center">
            <button
              className="text-gray-500 hover:text-gray-700 text-sm"
              onClick={onClose}
            >
              Cerrar
            </button>
          </div>
        </div>
      </div>
    )
  );
};

export default OTPModal;

